import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import PushPanelShell from './PushPanelShell';
import type { PushDeliveryLog, PushDeliveryStatusCounts } from '../../../models/MasterAdmin/pushNotificationHub';
import { PUSH_HUB_BASE_PATH } from '../../../models/MasterAdmin/pushNotificationHub';
import { fetchPushDeliveries, fetchPushDeliveryStats } from '../../../services/MasterAdmin/pushNotificationHubService';
import { getApiErrorMessage } from '../../../utils/apiErrorMessage';

const EMPTY_COUNTS: PushDeliveryStatusCounts = {
  queued: 0,
  sent: 0,
  delivered: 0,
  read: 0,
  failed: 0,
  skipped: 0,
  total: 0,
};

const STATUS_COLORS: Record<string, string> = {
  queued: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200',
  sent: 'bg-sky-100 text-sky-800 dark:bg-sky-950/50 dark:text-sky-200',
  delivered: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200',
  read: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950/50 dark:text-indigo-200',
  failed: 'bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-200',
  skipped: 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-200',
};

function formatWhen(iso: string | null | undefined) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'medium', timeStyle: 'short' });
}

const PushDeliveryPanel: React.FC = () => {
  const [items, setItems] = useState<PushDeliveryLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [counts, setCounts] = useState<PushDeliveryStatusCounts>(EMPTY_COUNTS);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [days, setDays] = useState(30);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [recipientType, setRecipientType] = useState('');

  useEffect(() => {
    setLoading(true);
    const params = {
      page,
      limit: 50,
      days,
      ...(search ? { search } : {}),
      ...(status ? { status } : {}),
      ...(recipientType ? { recipientType } : {}),
    };
    Promise.all([fetchPushDeliveries(params), fetchPushDeliveryStats(params)])
      .then(([listRes, statsRes]) => {
        setItems(listRes.data.items || []);
        setTotal(listRes.data.pagination?.totalItems || 0);
        setTotalPages(listRes.data.pagination?.totalPages || 1);
        setCounts({ ...EMPTY_COUNTS, ...(statsRes.data || {}) });
      })
      .catch((err: unknown) => {
        toast.error(getApiErrorMessage(err, 'Failed to load delivery log'));
        setItems([]);
        setCounts(EMPTY_COUNTS);
      })
      .finally(() => setLoading(false));
  }, [page, status, days, search, recipientType]);

  const cards = [
    { key: '', label: 'Total', value: counts.total, activeClass: 'border-gray-900 ring-1 ring-gray-900 dark:border-white dark:ring-white' },
    { key: 'SENT', label: 'Sent', value: counts.sent, activeClass: 'border-sky-600 ring-1 ring-sky-600' },
    { key: 'DELIVERED', label: 'Delivered', value: counts.delivered || 0, activeClass: 'border-emerald-600 ring-1 ring-emerald-600' },
    { key: 'READ', label: 'Read', value: counts.read || 0, activeClass: 'border-indigo-600 ring-1 ring-indigo-600' },
    { key: 'FAILED', label: 'Failed', value: counts.failed, activeClass: 'border-red-600 ring-1 ring-red-600' },
    { key: 'QUEUED', label: 'Queued', value: counts.queued, activeClass: 'border-gray-500 ring-1 ring-gray-500' },
    { key: 'SKIPPED', label: 'Skipped', value: counts.skipped, activeClass: 'border-amber-600 ring-1 ring-amber-600' },
  ];

  return (
    <PushPanelShell
      title="Delivery log"
      description="FCM attempts with status (sent → delivered → read on tap). Errors show a clear message."
    >
      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
        {cards.map((card) => {
          const active = status === card.key;
          return (
            <button
              key={card.label}
              type="button"
              onClick={() => {
                setStatus(card.key);
                setPage(1);
              }}
              className={`rounded-xl border bg-white p-3 text-left dark:bg-gray-900 ${
                active ? card.activeClass : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">{card.label}</p>
              <p className="mt-1 text-xl font-bold text-gray-900 dark:text-white">{card.value}</p>
            </button>
          );
        })}
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <input
          type="search"
          placeholder="Search name, phone, campaign…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setSearch(searchInput.trim());
              setPage(1);
            }
          }}
          className="min-w-[220px] rounded-lg border px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
        />
        <button
          type="button"
          onClick={() => {
            setSearch(searchInput.trim());
            setPage(1);
          }}
          className="rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-white dark:bg-gray-100 dark:text-gray-900"
        >
          Search
        </button>
        <select
          value={recipientType}
          onChange={(e) => {
            setRecipientType(e.target.value);
            setPage(1);
          }}
          className="rounded-lg border px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
        >
          <option value="">All recipients</option>
          <option value="USER">Users</option>
          <option value="VENDOR">Vendors</option>
          <option value="GUEST">Guests</option>
        </select>
        <select
          value={days}
          onChange={(e) => {
            setDays(Number(e.target.value));
            setPage(1);
          }}
          className="rounded-lg border px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={0}>All time</option>
        </select>
      </div>

      <p className="mb-2 text-xs text-gray-500">{total.toLocaleString()} attempts</p>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900/50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Recipient</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Campaign / Template</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Error</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Platform</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">When</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-gray-500">
                  No deliveries yet. Send a campaign to populate this log.
                </td>
              </tr>
            ) : (
              items.map((row) => {
                const display = row.displayStatus || row.status;
                return (
                  <tr key={row.id}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900 dark:text-white">{row.recipientName}</p>
                      <p className="text-xs text-gray-500">
                        {row.recipientType}
                        {row.recipientPhone ? ` · ${row.recipientPhone}` : ''}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`${PUSH_HUB_BASE_PATH}/campaigns/${row.campaignId}`}
                        className="font-medium text-blue-600 hover:underline"
                      >
                        {row.campaignTitle || row.campaignId}
                      </Link>
                      <p className="mt-0.5 text-xs text-gray-500">
                        Template: {row.templateName || '—'}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${
                          STATUS_COLORS[display.toLowerCase()] || STATUS_COLORS.queued
                        }`}
                      >
                        {display}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-red-600 dark:text-red-400">
                      {row.errorMessage || row.fcmErrorCode || row.skipReason || '—'}
                    </td>
                    <td className="px-4 py-3 text-xs">{row.platform || '—'}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{formatWhen(row.sentAt || row.createdAt)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 ? (
        <div className="mt-3 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Page {page} of {totalPages}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-lg border px-3 py-1 text-sm disabled:opacity-40 dark:border-gray-600"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-lg border px-3 py-1 text-sm disabled:opacity-40 dark:border-gray-600"
            >
              Next
            </button>
          </div>
        </div>
      ) : null}
    </PushPanelShell>
  );
};

export default PushDeliveryPanel;
